'use client'
import { Button } from '@heroui/button'
import { Chip } from '@heroui/chip'
import { Input } from '@heroui/input'
import { Modal, ModalContent } from '@heroui/modal'
import { useEffect, useRef, useState } from 'react'

import { ArrowLeftIcon, CheckCircleIcon, PlusIcon } from '@/components/shared'
import { Typography } from '@/components/ui'

enum AddTagModalVariant {
  Add = 'add',
  View = 'view',
}

interface AddTagModalProps {
  editable?: boolean
  isOpen: boolean
  tags?: string[]
  onSaveTag: (tag: string) => void
  onDeleteTag: (tag: string) => void
  onOpenChange: (isOpen: boolean) => void
}

interface ContentCreateProps {
  tag: string
  onChangeTag: (tag: string) => void
  onSaveTag: () => void
}

const ContentCreate = ({ tag, onChangeTag, onSaveTag }: ContentCreateProps) => {
  const inputRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [])
  return (
    <div className="flex w-full flex-1 flex-col pt-6">
      <Input
        ref={inputRef}
        type="text"
        size="lg"
        value={tag}
        variant="bordered"
        radius="md"
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            onSaveTag()
          }
        }}
        onValueChange={onChangeTag}
        placeholder="Introduce tag here..."
      />
    </div>
  )
}

const ContentView = ({
  tags,
  removeTag,
  editable = true,
}: {
  tags: string[]
  removeTag: (tag: string) => void
  editable?: boolean
}) => {
  if (!tags.length) {
    return (
      <div className="flex h-full w-full flex-1 justify-center pt-24">
        <Typography className="text-content4-foreground">{`This moment doesn’t have tags for now`}</Typography>
      </div>
    )
  }
  return (
    <div className="flex h-fit w-full flex-wrap gap-3 pt-6">
      {tags.map((tag) => (
        <Chip
          key={tag}
          variant="flat"
          radius="sm"
          color="primary"
          className="rounded-lg bg-primary-100"
          onClose={editable ? () => removeTag(tag) : undefined}
        >
          {tag}
        </Chip>
      ))}
    </div>
  )
}

const AddTagModal = ({
  isOpen,
  onOpenChange,
  tags = [],
  editable = true,
  onSaveTag,
  onDeleteTag,
}: AddTagModalProps) => {
  const [variant, setVariant] = useState<AddTagModalVariant>(
    AddTagModalVariant.View,
  )
  const [tagValue, setTagValue] = useState<string>('')
  const isAddVariant = variant === AddTagModalVariant.Add

  const handleHeaderClick = (onClose: () => void) => {
    if (isAddVariant) {
      return setVariant(AddTagModalVariant.View)
    }
    return onClose()
  }

  const handleSaveTag = () => {
    onSaveTag(tagValue)
    setTagValue('')
    setVariant(AddTagModalVariant.View)
  }

  const handleRemoveTag = (tag: string) => {
    onDeleteTag(tag)
  }

  const handleActionHeaderClick = () => {
    if (isAddVariant) {
      return handleSaveTag()
    }
    return setVariant(AddTagModalVariant.Add)
  }

  const renderHeaderIcon = isAddVariant ? 'Cancel' : 'Back'
  const renderActionHeaderLabel = isAddVariant ? 'Add' : 'New'
  const renderActionHeaderIcon = isAddVariant ? (
    <CheckCircleIcon className="text-2xl" />
  ) : (
    <PlusIcon className="text-2xl" />
  )
  const actionHeaderIconDisabled = isAddVariant && !tagValue

  return (
    <Modal
      size="full"
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      hideCloseButton
      scrollBehavior="outside"
      classNames={{
        base: 'my-0',
      }}
    >
      <ModalContent>
        {(onClose) => (
          <div className="mx-auto flex h-full w-full max-w-[640px] flex-col px-4 py-3">
            <div className="row flex items-center justify-between">
              <div className="row flex items-center gap-4">
                <Button
                  id="header-icon"
                  aria-label="back"
                  variant="solid"
                  radius="full"
                  className="h-10 w-10 bg-primary-100 text-default-foreground"
                  isIconOnly
                  onClick={(e) => {
                    e.preventDefault()
                    handleHeaderClick(onClose)
                  }}
                >
                  <ArrowLeftIcon className="text-2xl" />
                </Button>
                {renderHeaderIcon}
              </div>
              <Button
                id="header-action-icon"
                size="md"
                endContent={renderActionHeaderIcon}
                variant={isAddVariant ? 'solid' : 'flat'}
                color={isAddVariant ? 'primary' : 'default'}
                className="rounded-[80px]"
                onClick={handleActionHeaderClick}
                isDisabled={actionHeaderIconDisabled}
              >
                {renderActionHeaderLabel}
              </Button>
            </div>
            <div className="flex h-full w-full flex-1">
              {isAddVariant ? (
                <ContentCreate
                  tag={tagValue}
                  onChangeTag={setTagValue}
                  onSaveTag={handleSaveTag}
                />
              ) : (
                <ContentView
                  tags={tags}
                  removeTag={handleRemoveTag}
                  editable={editable}
                />
              )}
            </div>
          </div>
        )}
      </ModalContent>
    </Modal>
  )
}

export default AddTagModal
